import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ipoService } from '../services/ipoService';
import LoadingSpinner from '../components/LoadingSpinner';

const IPOListPage = () => {
  const [ipos, setIpos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchIPOs = async () => {
      try {
        setLoading(true);
        const data = await ipoService.getAllIPOs();
        setIpos(data);
      } catch (err) {
        setError('Failed to load IPO data');
      } finally {
        setLoading(false);
      }
    };

    fetchIPOs();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="flex items-center justify-center min-h-screen text-red-600 text-lg">{error}</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              IPO Tracker
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-600 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors">
                Home
              </Link>
              <Link to="/market-watch" className="text-gray-600 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors">
                Market Watch
              </Link>
              <Link to="/ipos" className="bg-blue-100 text-blue-700 px-3 py-2 rounded-md text-sm font-medium">
                IPOs
              </Link>
            </div>
            <div className="md:hidden">
              <Link to="/" className="text-gray-600 hover:text-blue-600 p-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">IPO Listings</h1>
          <p className="text-gray-600">Discover and invest in upcoming Initial Public Offerings</p>
        </div>
        
        <div className="space-y-4">
          {ipos.map((ipo) => (
            <Link to={`/ipo/${ipo.id}`} key={ipo.id} className="block">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 hover:shadow-xl transition-shadow">
                {/* Mobile Layout */}
                <div className="md:hidden">
                  <div className="flex items-start space-x-4 mb-4">
                    <img src={ipo.companyLogo} alt={ipo.companyName} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900">{ipo.companyName}</h3>
                      <p className="text-sm text-gray-600">{ipo.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Issue Date</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {new Date(ipo.issueDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short'
                        })}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Issue Size</div>
                      <div className="text-sm font-semibold text-gray-900">{ipo.issueSize}</div>
                    </div>
                    
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Price Range</div>
                      <div className="text-sm font-semibold text-gray-900">{ipo.priceRange}</div>
                    </div>
                    
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Min Investment</div>
                      <div className="text-sm font-semibold text-gray-900">{ipo.minInvestment}</div>
                    </div>
                  </div>
                </div>
                
                {/* Desktop Layout */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <img src={ipo.companyLogo} alt={ipo.companyName} className="w-16 h-16 rounded-xl object-cover" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{ipo.companyName}</h3>
                      <p className="text-gray-600">{ipo.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-8">
                    <div className="text-center">
                      <div className="text-sm text-gray-500 mb-1">Issue Date</div>
                      <div className="font-semibold text-gray-900">
                        {new Date(ipo.issueDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-sm text-gray-500 mb-1">Issue Size</div>
                      <div className="font-semibold text-gray-900">{ipo.issueSize}</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-sm text-gray-500 mb-1">Price Range</div>
                      <div className="font-semibold text-gray-900">{ipo.priceRange}</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-sm text-gray-500 mb-1">Min Investment</div>
                      <div className="font-semibold text-gray-900">{ipo.minInvestment}</div>
                      <div className="text-xs text-gray-500">{ipo.quantity}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IPOListPage;